import * as PIXI from 'pixi.js';

class NodeElement<T> {
  public object: T;
  public bounds: PIXI.Rectangle;

  constructor(object: T, bounds: PIXI.Rectangle) {
    this.object = object;
    this.bounds = bounds;
  }
}

export class QuadtreeNode<T> {
  private topRight: QuadtreeNode<T> | null = null;
  private topLeft: QuadtreeNode<T> | null = null;
  private bottomRight: QuadtreeNode<T> | null = null;
  private bottomLeft: QuadtreeNode<T> | null = null;

  private bounds: PIXI.Rectangle;
  private minimumBounds: PIXI.Point;

  private objectContainer: NodeElement<T>[] = [];
  private objectsInBounds: T[] = [];

  constructor(bounds: PIXI.Rectangle, minimumBounds: PIXI.Point) {
    this.bounds = bounds;
    this.minimumBounds = minimumBounds;
  }

  public removeObject(object: T): void {
    for (let i = 0; i < this.objectContainer.length; i++) {
      const element = this.objectContainer[i];
      if (element.object === object) {
        this.objectContainer.splice(i, 1);
        return;
      }
    }
  }

  private objectInBoundsHelper(checkingBounds: PIXI.Rectangle, found: T[]): void {
    if (!checkingBounds.intersects(this.bounds)) {
      return; // Границы не пересекаются
    }

    for (const nodeElement of this.objectContainer) {
      if (checkingBounds.intersects(nodeElement.bounds)) {
        found.push(nodeElement.object);
      }
    }

    this.topLeft?.objectInBoundsHelper(checkingBounds, found);
    this.topRight?.objectInBoundsHelper(checkingBounds, found);
    this.bottomLeft?.objectInBoundsHelper(checkingBounds, found);
    this.bottomRight?.objectInBoundsHelper(checkingBounds, found);
  }

  public objectInBounds(checkingBounds: PIXI.Rectangle): T[] {
    this.objectsInBounds.length = 0;
    this.objectInBoundsHelper(checkingBounds, this.objectsInBounds);
    return this.objectsInBounds;
  }

  public insert(object: T, objectBounds: PIXI.Rectangle): QuadtreeNode<T> | null {
    if (!this.bounds.intersects(objectBounds)) {
      return null; // Объект не подходит по границам
    }

    // console.log("insert");

    const newWidth = this.bounds.width / 2;
    const newHeight = this.bounds.height / 2;

    if (newHeight < this.minimumBounds.x || newWidth < this.minimumBounds.y) {
      this.objectContainer.push(new NodeElement(object, objectBounds));
      return this;
    }

    let intersectCount = 0;
    let lastIntersection: PIXI.Rectangle | null = null;

    const subBoundTopLeft = new PIXI.Rectangle(this.bounds.x, this.bounds.y, newWidth, newHeight);
    if (subBoundTopLeft.intersects(objectBounds)) {
      intersectCount++;
      lastIntersection = subBoundTopLeft;
    }

    const subBoundTopRight = new PIXI.Rectangle(this.bounds.x + newWidth, this.bounds.y, newWidth, newHeight);
    if (subBoundTopRight.intersects(objectBounds)) {
      intersectCount++;
      lastIntersection = subBoundTopRight;
    }

    const subBoundBottomLeft = new PIXI.Rectangle(this.bounds.x, this.bounds.y + newHeight, newWidth, newHeight);
    if (subBoundBottomLeft.intersects(objectBounds)) {
      intersectCount++;
      lastIntersection = subBoundBottomLeft;
    }

    const subBoundBottomRight = new PIXI.Rectangle(this.bounds.x + newWidth, this.bounds.y + newHeight, newWidth, newHeight);
    if (subBoundBottomRight.intersects(objectBounds)) {
      intersectCount++;
      lastIntersection = subBoundBottomRight;
    }

    if (intersectCount === 1) {
      if (lastIntersection === subBoundTopLeft) {
        if (!this.topLeft) {
          this.topLeft = new QuadtreeNode<T>(lastIntersection, this.minimumBounds);
        }
        return this.topLeft.insert(object, objectBounds);
      }
      if (lastIntersection === subBoundTopRight) {
        if (!this.topRight) {
          this.topRight = new QuadtreeNode<T>(lastIntersection, this.minimumBounds);
        }
        return this.topRight.insert(object, objectBounds);
      }
      if (lastIntersection === subBoundBottomLeft) {
        if (!this.bottomLeft) {
          this.bottomLeft = new QuadtreeNode<T>(lastIntersection, this.minimumBounds);
        }
        return this.bottomLeft.insert(object, objectBounds);
      }
      if (lastIntersection === subBoundBottomRight) {
        if (!this.bottomRight) {
          this.bottomRight = new QuadtreeNode<T>(lastIntersection, this.minimumBounds);
        }
        return this.bottomRight.insert(object, objectBounds);
      }
    } else if (intersectCount > 1) {
      this.objectContainer.push(new NodeElement(object, objectBounds));
      return this;
    }

    return null;
  }

  public getObjects(): T[] {
    return this.objectContainer.map(element => element.object);
  }

  public getBounds(): PIXI.Rectangle {
    return this.bounds;
  }
}
